import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { IconButton } from '@/components/Button';
import { ViewSidebar } from '@/components/ViewSidebar';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { date, daysUntil, qty } from '@/lib/format';
import { Applied, Chips, DataTable, Denied, FilterButton, k, Pager, PhoneBar, SearchBox, Sentinel, type ChipItem, type Col } from './ListKit';
import { FilterSheet } from './FilterSheet';
import { InventoryActionsSheet } from './InventoryActionsSheet';
import { Avail } from './StockCards';
import { usePagedList, useUrlState, useWarehouses } from './api';
import s from './ShortagePage.module.css';

type ShortageOrder = { id: string; orderNo: string };
type ShortagePurchase = { id: string; purchaseNo: string; status: 'ORDERED' | 'PARTIALLY_RECEIVED'; remainingQuantity: number; expectedArrivalDate: string | null };
type ShortageRow = {
  productId: string; productCode: string; productName: string;
  warehouseId: string; warehouseCode: string; warehouseName: string;
  currentQuantity: number; reservedQuantity: number; availableQuantity: number;
  affectedOrders: ShortageOrder[]; transitOrders: ShortagePurchase[];
};
type ShortageStats = { deficitQuantity: number; affectedOrderCountOnPage: number };

function rowKey(row: ShortageRow) { return `${row.productId}:${row.warehouseId}`; }

function transitLabel(row: ShortageRow) {
  const po = row.transitOrders[0];
  if (!po) return <Dot tone="red">无在途采购</Dot>;
  if (po.status === 'PARTIALLY_RECEIVED') return <Dot tone="amber"><span className="tnum">{po.purchaseNo}</span> · 剩余 {qty(po.remainingQuantity)}</Dot>;
  if (daysUntil(po.expectedArrivalDate) === 0) return <Dot tone="green"><span className="tnum">{po.purchaseNo}</span> · 今日到货 {qty(po.remainingQuantity)}</Dot>;
  return <Dot tone="blue"><span className="tnum">{po.purchaseNo}</span> · {po.expectedArrivalDate ? `预计 ${date(po.expectedArrivalDate)}` : '未填到货日'} · 待入 {qty(po.remainingQuantity)}</Dot>;
}

function csvCell(value: string | number) {
  const text = String(value);
  const safe = typeof value === 'string' && /^[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

/** 负可用 L3：按产品 × 仓库排缺口；订单和在途信息取 V1 共享业务数据。 */
export function ShortagePage() {
  const navigate = useNavigate();
  const ff = useFormFactor();
  const can = useSession((state) => state.can);
  const allowed = can('inventory.read');
  const canPurchase = can('purchase.read');
  const canSuggest = can('purchase.suggestions.read');
  const canAdjust = import.meta.env.VITE_READONLY === '0' && can('inventory.adjust.write');
  const canInbound = import.meta.env.VITE_READONLY === '0' && can('inventory.in.write');
  const canImport = import.meta.env.VITE_READONLY === '0' && can('inventory.import.initial_stock');
  const canOutbound = import.meta.env.VITE_READONLY === '0' && can('inventory.out.write');
  const [sp, set] = useUrlState();
  const q = sp.get('q') || '';
  const warehouseId = sp.get('warehouseId') || undefined;
  const page = Number(sp.get('page')) || 1;
  const phone = ff === 'phone';
  const pageSize = phone ? 20 : 50;
  const [filterOpen, setFilterOpen] = useState(false);
  const [searching, setSearching] = useState(!!q);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [shortageSortDesc, setShortageSortDesc] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState('');
  const warehouses = useWarehouses(allowed);
  const list = usePagedList<ShortageRow, ShortageStats>(allowed ? '/api/v2/inventory/shortage' : null,
    { keyword: q || undefined, warehouseId }, { pageSize, append: phone, page });

  if (!allowed) return <Denied what="查看负可用预警" perm="查看库存" />;

  const summary = `${list.total} 个产品 × 仓库 · 合计缺口 ${qty(list.stats?.deficitQuantity || 0)} 件`;
  const impact = list.stats && `${list.total > list.items.length ? '当前页' : ''}影响 ${list.stats.affectedOrderCountOnPage} 张可见订单`;
  const chips: ChipItem[] = [
    { key: 'all', label: '库存总览', to: '/inventory' },
    { key: 'shortage', label: '负可用', count: list.total, active: true },
    ...(can('inventory.transactions.read') ? [{ key: 'tx', label: '出入库流水', to: '/inventory/transactions' }] : []),
    ...(canPurchase ? [{ key: 'transit', label: '在途待入库', to: '/inventory/transit' }] : []),
    { key: 'warehouse', label: '按仓库明细', to: '/inventory/warehouses' },
    { key: 'change', label: '变动汇总', to: '/inventory/changes' },
  ];
  const purchaseAction = (row: ShortageRow) => {
    const po = row.transitOrders[0];
    if (po && canPurchase) return <Link className={s.primary} to={`/purchase-orders/${po.id}`}>{po.status === 'PARTIALLY_RECEIVED' ? '继续入库' : '转入库'}</Link>;
    if (canSuggest) return <Link className={s.primary} to={`/purchase-suggestions?q=${encodeURIComponent(row.productCode)}`}>去采购</Link>;
    return <span className={s.disabled}>无采购权限</span>;
  };
  const viewOrders = (row: ShortageRow) => (
    <button type="button" className={s.secondary} disabled={!row.affectedOrders.length} onClick={() => setSelected((current) => current === rowKey(row) ? null : rowKey(row))}>
      看订单
    </button>
  );
  const orderPanel = (row: ShortageRow) => selected === rowKey(row) && (
    <div className={s.orderPanel}>
      <b>{row.productName} · {row.warehouseName} · 影响 {row.affectedOrders.length} 张订单</b>
      <div>{row.affectedOrders.map((order) => <Link key={order.id} className="tnum" to={`/orders/${order.id}`}>{order.orderNo}</Link>)}</div>
    </div>
  );
  const cols: Col<ShortageRow>[] = [
    { key: 'code', title: '产品编号', w: 136, fix: true, render: (row) => <span className={`${k.productCode} tnum`}>{row.productCode}</span> },
    { key: 'name', title: '产品名称', w: 190, render: (row) => row.productName },
    { key: 'wh', title: '仓库', w: 130, render: (row) => row.warehouseName },
    { key: 'cur', title: '现有', w: 82, align: 'r', render: (row) => qty(row.currentQuantity) },
    { key: 'res', title: '预占', w: 82, align: 'r', render: (row) => qty(row.reservedQuantity) },
    { key: 'av', title: '可用', w: 82, align: 'r', render: (row) => <Avail value={row.availableQuantity} strong /> },
    { key: 'orders', title: '影响订单', w: 86, align: 'r', render: (row) => row.affectedOrders.length },
    { key: 'transit', title: '在途采购', w: 260, render: transitLabel },
    { key: 'act', title: '操作', w: 164, render: (row) => <div className={s.actions}>{viewOrders(row)}{purchaseAction(row)}</div> },
  ];
  const search = <SearchBox value={q} placeholder="产品编号 / 名称 / 仓库" onSearch={(value) => set({ q: value || undefined, page: undefined })} />;
  const filter = <FilterButton boxed count={Number(!!warehouseId)} onClick={() => setFilterOpen(true)} />;
  const applied = <Applied items={warehouseId ? [{ key: 'warehouse', label: warehouses.find((warehouse) => warehouse.id === warehouseId)?.name || '指定仓库', onRemove: () => set({ warehouseId: undefined, page: undefined }) }] : []} onClear={() => set({ warehouseId: undefined, page: undefined })} />;
  const filterSheet = filterOpen && <FilterSheet sheet={phone} value={{ warehouseId }} onClose={() => setFilterOpen(false)} onApply={(value) => { setFilterOpen(false); set({ warehouseId: value.warehouseId, page: undefined }); }} groups={[{ key: 'warehouseId', title: '仓库', opts: warehouses.map((warehouse) => ({ value: warehouse.id, label: warehouse.name })) }]} />;
  const actionsSheet = actionsOpen && <InventoryActionsSheet
    warehouseName={warehouses.find((warehouse) => warehouse.id === warehouseId)?.name || warehouses.find((warehouse) => warehouse.isDefaultInbound)?.name || warehouses[0]?.name}
    allowed={{ inbound: canInbound, outbound: canOutbound, adjust: canAdjust, import: canImport }}
    onClose={() => setActionsOpen(false)}
    onSelect={(action) => { setActionsOpen(false); navigate(`/inventory?action=${action}`); }}
  />;
  const explain = ff === 'tablet'
    ? <div className={s.tabletExplain}><Icon name="info" size={18} /><div><b>预占超过现有库存的产品。负数是刻意允许的：先接单，再采购。</b><span>缺口会进入采购建议；入库、调整和采购操作按当前账号权限提供。</span></div></div>
    : <div className={s.explain}>负数是刻意允许的：先接单，再采购。入库、调整和采购操作仍可按权限继续。</div>;
  const suggestionButton = canSuggest && <Link className={s.suggest} to="/purchase-suggestions">一键生成采购建议</Link>;
  const body = list.error && !list.items.length ? <ErrorState title="负可用加载失败" message={list.error.message} detail="GET /api/v2/inventory/shortage" onRetry={list.reload} />
    : list.loading && !list.items.length ? <div className={k.pad}><Skeleton lines={4} /><Skeleton lines={4} /></div>
      : !list.items.length ? <Empty icon="ok" title="没有负可用的产品">当前没有产品在某个仓库的预占超过现有库存。</Empty> : null;
  const tabletCols: Col<ShortageRow>[] = cols.map((col) => ({
    ...col,
    w: col.w ? Math.round(col.w * 0.78) : undefined,
  }));
  const table = <DataTable dense={ff === 'tablet'} cols={ff === 'tablet' ? tabletCols : cols} rows={list.items} rowKey={rowKey} />;
  const activeRow = list.items.find((row) => rowKey(row) === selected);

  async function exportAll() {
    setExportBusy(true);
    setExportError('');
    try {
      const all: ShortageRow[] = [];
      let next = 1;
      for (;;) {
        const result = await api.get<{ items: ShortageRow[]; pagination: { hasNext: boolean } }>('/api/v2/inventory/shortage', { keyword: q || undefined, warehouseId, page: next, pageSize: 100 });
        all.push(...result.items);
        if (!result.pagination.hasNext) break;
        next += 1;
      }
      const lines = [
        ['产品编号', '产品名称', '仓库', '现有', '预占', '可用', '影响订单', '在途采购'],
        ...all.map((row) => [row.productCode, row.productName, row.warehouseName, row.currentQuantity, row.reservedQuantity, row.availableQuantity, row.affectedOrders.map((order) => order.orderNo).join('、'), row.transitOrders.map((order) => order.purchaseNo).join('、')]),
      ];
      const content = `\uFEFF${lines.map((line) => line.map(csvCell).join(',')).join('\r\n')}`;
      const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `负可用-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : '导出失败');
    } finally {
      setExportBusy(false);
    }
  }

  const phoneRows = list.items.slice().sort((a, b) => shortageSortDesc
    ? a.availableQuantity - b.availableQuantity
    : b.availableQuantity - a.availableQuantity);
  const phoneExplain = <div className={s.phoneExplain}>
    <Icon name="info" size={18} />
    <div><b>负数是刻意允许的：先接单，再采购</b>
      <p>{list.total} 个产品 · 合计缺口 {qty(list.stats?.deficitQuantity || 0)} 件 · 影响 {list.stats?.affectedOrderCountOnPage || 0} 张订单{list.total > list.items.length ? '（当前页）' : ''}</p>
      {suggestionButton}</div>
  </div>;
  const showInventoryActions = canAdjust || canInbound || canImport || canOutbound;
  if (phone) return <div className={k.phone}>
    <PhoneBar title="库存" actions={<>
      <IconButton name="srch" label="搜索" onClick={() => setSearching((value) => !value)} />
      {showInventoryActions && <IconButton name="plus" label="库存操作" onClick={() => setActionsOpen(true)} />}
    </>} />
    {(searching || q) && <div className={k.psearch}><SearchBox value={q} autoFocus={searching && !q} placeholder="产品编号 / 名称 / 仓库" onSearch={(value) => set({ q: value || undefined, page: undefined })} /></div>}
    <div className={k.chipbar}><Chips items={chips} inset="phone" /><FilterButton count={Number(!!warehouseId)} onClick={() => setFilterOpen(true)} /></div>
    {phoneExplain}{applied}
    <div className={k.pbody}>
      <div className={k.listbar}><span>{list.total} 个产品</span><button type="button" className={s.sortButton} onClick={() => setShortageSortDesc((value) => !value)}>{shortageSortDesc ? '缺口从大到小' : '缺口从小到大'}<Icon name="sort" size={14} /></button></div>
      {body || phoneRows.map((row) => <section key={rowKey(row)} className={s.card}>
        <div className={s.cardHead}><b>{row.productName}</b><span>缺 {qty(-row.availableQuantity)}</span></div>
        <p className={s.meta}><span className="tnum">{row.productCode}</span> · {row.warehouseName}</p>
        <div className={s.quantities}><span>现有 <b>{qty(row.currentQuantity)}</b></span><span>预占 <b>{qty(row.reservedQuantity)}</b></span><span>可用 <b><Avail value={row.availableQuantity} /></b></span></div>
        <div className={s.cardFoot}><div>{transitLabel(row)}<small>影响 {row.affectedOrders.length} 张订单</small></div><div className={s.actions}>{viewOrders(row)}{purchaseAction(row)}</div></div>
        {orderPanel(row)}
      </section>)}
      <Sentinel hasNext={list.hasNext} loading={list.loading} count={list.items.length} onMore={list.loadMore} />
    </div>{filterSheet}{actionsSheet}
  </div>;

  const content = <>{explain}{applied}{body ? <div className={k.dbody}>{body}</div> : table}{activeRow && orderPanel(activeRow)}{!body && <Pager page={list.page} pageSize={pageSize} total={list.total} shown={list.items.length} totalPages={list.totalPages} onGo={(next) => set({ page: next > 1 ? String(next) : undefined })} extra={<span>{summary}{impact && ` · ${impact}`}</span>} />}{filterSheet}</>;
  if (ff === 'desktop') return <div className={`${k.desk} ${k.desktopPage}`}>
    <header className={k.desktopHead}><div><h1>负可用</h1><p>预占超过现有库存的产品。负数是刻意允许的：先接单再采购<span>{summary}{impact && ` · ${impact}`}</span></p></div><div className={k.desktopActions}>{suggestionButton}</div></header>
    <div className={k.desktopWork}><ViewSidebar title="视图" active="shortage" onPick={() => undefined} views={[
      { key: 'all', label: '库存总览', icon: 'box', to: '/inventory' },
      { key: 'warehouse', label: '按仓库明细', icon: 'bank', to: '/inventory/warehouses' },
      { key: 'change', label: '变动汇总', icon: 'chart', to: '/inventory/changes' },
      { key: 'shortage', label: '负可用', icon: 'alert', count: list.total, hot: true, separator: true },
      { key: 'transit', label: '在途待入库', icon: 'inbox', ...(canPurchase ? { to: '/inventory/transit' } : { disabledReason: '当前账号没有采购单查看权限' }) },
      { key: 'noStock', label: '无实际库存', icon: 'flag', to: '/inventory?view=noStock' },
      ...(can('inventory.transactions.read') ? [{ key: 'tx', label: '出入库流水', icon: 'book', to: '/inventory/transactions', separator: true }] : []),
    ]} /><section className={k.desktopArea}><div className={k.desktopToolbar}><span className={`${s.sort} ${s.desktopSort}`}>按缺口从大到小排列</span></div>{applied}{body ? <div className={k.dbody}>{body}</div> : table}{activeRow && orderPanel(activeRow)}{!body && <Pager page={list.page} pageSize={pageSize} total={list.total} shown={list.items.length} totalPages={list.totalPages} onGo={(next) => set({ page: next > 1 ? String(next) : undefined })} extra={<span>{summary}{impact && ` · ${impact}`}</span>} />}{filterSheet}</section></div>
    {exportError && <div className={s.exportError} role="alert">{exportError}</div>}
  </div>;
  return <div className={k.desk}>
    <header className={k.dh}><h1>负可用<small>{list.total} 条</small></h1>{ff === 'tablet' && <span className={k.sp} />} {ff === 'tablet' && <span className={s.sort}>缺口从大到小 <b aria-hidden="true">↕</b></span>}{ff !== 'tablet' && <span className={k.sp} />}{ff !== 'tablet' && <>{search}{filter}</>}<button type="button" className={k.linkBtn} disabled={exportBusy} onClick={() => void exportAll()}>{exportBusy ? '导出中…' : '导出'}</button>{suggestionButton}</header>
    {content}{exportError && <div className={s.exportError} role="alert">{exportError}</div>}
  </div>;
}
